import { track } from "@amplitude/analytics-react-native";
import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import GetGatheringThemeAndSuggestionsDocument from "src/api/graphql/__generated__/documents/GetGatheringThemeAndSuggestionsDocument";
import SetGatheringThemeDocument from "src/api/graphql/__generated__/documents/SetGatheringThemeDocument";
import { BottomPopInMenuProps } from "src/components/BottomPopInMenu/BottomPopInMenu";
import { parsePalette } from "src/components/FloatingThemeMenu/helpers/parsePalette";
import { FloatingThemeMenuProps } from "src/components/FloatingThemeMenu/types";
import { DefaultPalette } from "src/constants/DefaultPalette";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import { useGatheringStore } from "src/contexts/GatheringContext";
import haptic from "src/helpers/haptic";
import parseStickerTemplateUrl from "src/helpers/parseV0TemplateUrl/parseStickerTemplateUrl";
import handleRequestTemplate from "src/helpers/themeHelpers/handleRequestTemplate";
import isPaletteMatch from "src/helpers/themeHelpers/isPaletteMatch";
import parseOptionsFromThemeSuggestions from "src/helpers/themeHelpers/parseOptionsFromThemeSuggestions";
import { useModal } from "src/modals";
import InternalModal from "src/modals/configs/InternalModal";
import {
	CustomThemeModalProps,
	ThemeOptionItem,
} from "src/screens/(internal)/CustomThemeModal/types";
import useRenderGatheringThemePreviewHeaderItem from "./useRenderGatheringThemePreviewHeaderItem";

const FLOATING_MENU_GROUP: BottomPopInMenuProps["group"] = "GatheringMenu";
const usePresentGatheringThemeEdit = () => {
	const { presentModal } = useModal<InternalModal>();
	const apolloClient = useApolloClient();
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const { renderPreviewHeaderItem } = useRenderGatheringThemePreviewHeaderItem();

	const { data } = useQuery(GetGatheringThemeAndSuggestionsDocument, {
		fetchPolicy: "cache-and-network",
		variables: {
			getGatheringInput: { gatheringId },
			getThemeSuggestionsInput: { gatheringId },
		},
	});
	const { gatheringData, suggestionData } = useMemo(
		() => ({
			gatheringData: data?.getGathering,
			suggestionData: data?.getThemeSuggestions,
		}),
		[data],
	);

	const suggestions = useMemo(() => {
		return [
			{
				presetId: null,
				palette: DefaultPalette,
				stickerPack: undefined,
				reaction: undefined,
			},
			...(suggestionData ?? []).map((suggestion) => ({
				...suggestion,
			})),
		];
	}, [suggestionData]);

	const initialThumbnail: CustomThemeModalProps["initialThumbnail"] = useMemo(
		() => ({
			readUrl: parseGatheringThumbnailUrl({ readUrl: gatheringData?.thumbnail?.readUrl }),
		}),
		[gatheringData?.thumbnail],
	);

	const currentTheme: CustomThemeModalProps["initialSelection"] = useMemo(
		() => ({
			palette: parsePalette(gatheringData?.palette) ?? DefaultPalette,
			stickerPack: gatheringData?.stickerPack ?? undefined,
			reaction: gatheringData?.reactionAsset?.emoji ?? undefined,
		}),
		[gatheringData],
	);

	const handleApplyTheme: CustomThemeModalProps["handleApplyTheme"] = useCallback(
		async ({ item: { palette, stickerPack, reaction } }: { item: ThemeOptionItem }) => {
			await apolloClient.mutate({
				mutation: SetGatheringThemeDocument,
				variables: {
					input: {
						gatheringId,
						palette,
						stickerPackId: stickerPack?.stickerPackId,
						reaction: reaction ? { emoji: reaction } : undefined,
					},
				},
			});
		},
		[apolloClient, gatheringId],
	);

	const onSelect: CustomThemeModalProps["onSelect"] = useCallback(
		({ palette, stickerPack, reaction }: ThemeOptionItem) => {
			track("gathering_customTheme_select", {
				gatheringId,
				stickerPackId: stickerPack?.stickerPackId,
				palette,
				reaction,
			});
		},
		[gatheringId],
	);

	const openModal = useCallback(() => {
		const { paletteOptions, reactionOptions, stickerOptions, templateOptions } =
			parseOptionsFromThemeSuggestions({ suggestions });

		const onShuffle = (item?: ThemeOptionItem) => {
			if (item) {
				const { palette, stickerPack, reaction } = item;
				track("gathering_customTheme_random", {
					gatheringId,
					stickerPackId: stickerPack?.stickerPackId,
					palette,
					reaction,
				});
			} else {
				track("gathering_customTheme_random", {
					gatheringId,
				});
			}
		};

		track("gathering_customTheme_openModal", {
			gatheringId,
		});
		presentModal("CustomThemeModal", {
			props: {
				categoryProps: [
					{
						categoryName: "template",
						options: templateOptions,
						request: {
							enabled: true,
							onPress: async () => {
								await handleRequestTemplate({
									apolloClient,
									gatheringId,
									onPressSubmit: () => {
										track("gathering_templateRequest_submit", { gatheringId });
									},
									onPressCancel: () => {
										track("gathering_templateRequest_cancel", { gatheringId });
									},
								});
							},
						},
					},
					{ categoryName: "palette", options: paletteOptions },
					{ categoryName: "stickerPack", options: stickerOptions },
					{ categoryName: "reaction", options: reactionOptions },
				],
				initialSelection: currentTheme,
				onShuffle,
				onSelect,
				handleApplyTheme,
				initialThumbnail,
				renderPreviewHeaderItem,
			},
		});
	}, [
		presentModal,
		onSelect,
		handleApplyTheme,
		gatheringId,
		currentTheme,
		suggestions,
		apolloClient,
		initialThumbnail,
		renderPreviewHeaderItem,
	]);

	const floatingMenuOptions: ThemeOptionItem[] = useMemo(() => {
		return suggestions.map(
			(suggestion) =>
				({
					palette: parsePalette(suggestion.palette),
					stickerPack: suggestion.stickerPack
						? {
								presetId: suggestion.presetId ?? undefined,
								stickerPackId: suggestion.stickerPack?.stickerPackId,
								thumbnailUrl: parseStickerTemplateUrl({
									readUrl: suggestion.stickerPack?.stickerThumbnail?.readUrl,
								}),
							}
						: undefined,
					reaction: suggestion.reaction?.emoji ?? undefined,
				}) satisfies ThemeOptionItem,
		);
	}, [suggestions]);

	const handleFloatingMenuSelect = useCallback(
		async (suggestion: ThemeOptionItem & { isSelected: boolean; isShuffle?: boolean }) => {
			if (
				!suggestion.palette ||
				!suggestion.palette.primary ||
				!suggestion.palette.secondary ||
				!suggestion.palette.tertiary
			) {
				return;
			}
			haptic("Medium");
			if (suggestion.isSelected) {
				track("gathering_selectedThemeReselected", {
					palette: suggestion.palette,
					stickerPackId: suggestion.stickerPack?.stickerPackId,
					reaction: suggestion.reaction,
				});
				return;
			}
			track("gathering_newSelectedTheme", {
				palette: suggestion.palette,
				isShuffle: suggestion.isShuffle,
				stickerPackId: suggestion.stickerPack?.stickerPackId,
				reaction: suggestion.reaction,
			});
			const parsedPalette = isPaletteMatch(suggestion.palette, DefaultPalette)
				? null
				: {
						primary: suggestion.palette.primary,
						secondary: suggestion.palette.secondary,
						tertiary: suggestion.palette.tertiary,
					};
			await handleApplyTheme({
				item: {
					palette: parsedPalette,
					stickerPack: suggestion.stickerPack ?? undefined,
					reaction: suggestion.reaction ?? undefined,
				},
			});
		},
		[handleApplyTheme],
	);

	return {
		floatingMenuProps: {
			currentTheme,
			group: FLOATING_MENU_GROUP,
			options: floatingMenuOptions,
			handlePress: handleFloatingMenuSelect,
			handleCustomizePress: openModal,
		} satisfies FloatingThemeMenuProps,
	};
};

export default usePresentGatheringThemeEdit;
